import React, { useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useMobileNavigation } from '../../../contexts/MobileNavigationContext';
import './styles/NavigationMobileMenu.css';
import homeIcon from '../../icon/home.png';
import productIcon from '../../icon/product.png';
import searchIcon from '../../icon/search.png';
import profileIcon from '../../icon/profile.png';

const NavigationMobileMenu: React.FC = () => {
  const [active, setActive] = useState<'home' | 'products' | 'search' | 'service' | 'profile'>('home');
  const [isVisible, setIsVisible] = useState(true);
  const { isNavigationHidden } = useMobileNavigation();
  const navigate = useNavigate();
  const location = useLocation();
  const prevScrollRef = useRef(0);
  const navRef = useRef<HTMLDivElement>(null);

  // Sync active tab with current route
  React.useEffect(() => {
    const p = location.pathname;
    if (p.startsWith('/categories/view-all')) {
      setActive('products');
    } else if (p.startsWith('/profile')) {
      setActive('profile');
    } else if (p.startsWith('/search')) {
      setActive('search');
    } else if (p === '/') {
      setActive('home');
    }
  }, [location.pathname]);

  // Show/hide on scroll (hide when scrolling down, show when scrolling up)
  React.useEffect(() => {
    const onScroll = () => {
      const current = window.pageYOffset || document.documentElement.scrollTop;
      const prev = prevScrollRef.current;
      const delta = Math.abs(current - prev);

      // Avoid jitter on tiny scrolls
      if (delta < 6) return;

      if (current > prev && current > 80) {
        // Scrolling down
        setIsVisible(false);
      } else {
        // Scrolling up
        setIsVisible(true);
      }
      prevScrollRef.current = current;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavClick = (item: 'home' | 'products' | 'search' | 'service' | 'profile') => {
    setActive(item);
    
    switch(item) {
      case 'home':
        navigate('/');
        break;
      case 'products':
        navigate('/categories/view-all');
        break;
      case 'search':
        navigate('/search');
        // Focus the search input after navigation
        setTimeout(() => {
          const searchInput = document.getElementById('search-input') as HTMLInputElement | null;
          searchInput?.focus();
        }, 100);
        break;
      case 'profile':
        navigate('/profile');
        break;
      case 'service':
        // Add service navigation if needed
        break;
    }
  };

  // Navigation items data with correct image paths
  const navItems = [
    { id: 'home', icon: homeIcon, label: 'Home' },
    { id: 'products', icon: productIcon, label: 'Products' },
    { id: 'search', icon: searchIcon, label: 'Search' },
    // { id: 'service', icon: serviceIcon, label: 'Service' },
    { id: 'profile', icon: profileIcon, label: 'Profile' },
  ];

  // Create a portal for the mobile menu to avoid overflow issues
  const mobileMenu = ( 
    <nav 
      ref={navRef}
      className={`navbar-card ${!isVisible || isNavigationHidden ? 'hidden' : ''}`}
    
    >
      <div className="nav-box">
        <ul className="nav-container">
          {navItems.map((item) => (
            <li 
              key={item.id}
              className={`nav__item ${active === item.id ? 'active' : ''}`}
              onClick={() => handleNavClick(item.id as any)}
            >
              <button className="nav__item-link">
                <div className="nav__item-icon">
                  <img 
                    className={`icon ${active === item.id ? 'active' : ''}`}
                    src={item.icon} 
                    alt={item.label} 
                  />
                </div>
                <span className="nav__item-text">{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );

  // Use a portal to render the menu at the document body level
  return createPortal(mobileMenu, document.body);
};

export default NavigationMobileMenu;